import { formatPrice } from "@keepoinc/site-page-react-component";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ConfirmModal from "../../../../components/ConfirmModal";
import Input from "../../../../components/Form/Input/input";
import { Label as LabelText } from "../../../../components/Form/Label/styles";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box } from "../../../../components/Layout";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { useCurrentPage, withPage } from "../../../../hooks/CurrentPage";
import {
  PRICING_MAX_ITEMS,
  PRICING_MAX_PAGE_TITLE,
  PRICING_MAX_TITLE,
  PricingItem,
  PricingSection,
} from "../../../../model/site";
import { clone, deepEqual } from "../../../../utils/objects";
import { uuidv4 } from "../../../../utils/uuid";
import ItemsTable, { Item } from "../../components/ItemsTable";
import { useSection } from "../../hooks/Section";
import { navigationState } from "../Sections/navigationState";
import { CurrencySelect } from "./components/CurrencySelect";
import { PricingNavigationState } from "./pricingNavigationState";
import { Container, LimitNote } from "./styles";

const prefix = "pages.editor.pricing";

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pageId } = useCurrentPage();

  const pricingNavigationState = useRef(navigationState.get() as PricingNavigationState | undefined);
  const restoreState = useCallback(
    (newly: unknown, section: PricingSection) => {
      const coming = pricingNavigationState.current?.currentSection;
      // back from the screen of a plan: the section as it was left, with what that screen changed
      if (coming?.type === "pricing") {
        section.pageTitle = coming.pageTitle;
        section.items = coming.items;
        section.currency = coming.currency;
      } else if (newly) {
        // a new section comes titled, as the other sections do (the title can be changed or cleared)
        section.pageTitle = t(`${prefix}.header.title`);
        section.items = [];
        section.currency = "BRL";
      }
      return section;
    },
    [t, pricingNavigationState]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<PricingSection>("pricing", restoreState);

  const [idRemoving, setIdRemoving] = useState<string>();
  const items = section.items || [];
  const currency = section.currency || "BRL";
  const isFull = items.length >= PRICING_MAX_ITEMS;

  // A plan as the list of items shows it: its name, and its price under it
  const toRow = ({ id, title, price, thumbnailType, image, emoji, active }: PricingItem): Item => ({
    id,
    title,
    description:
      price === undefined ? "" : price === 0 ? t(`${prefix}.form.free`) : formatPrice(price, currency, i18n.language),
    thumbnailType: thumbnailType || "NONE",
    image,
    emoji,
    active,
  });

  const goEditItemPage = (id?: string) => {
    if (!id && isFull) {
      return;
    }
    const previous = pricingNavigationState.current;
    const state: PricingNavigationState = {
      currentSection: clone<PricingSection>(section),
      defaultSection: previous?.defaultSection?.type === "pricing" ? previous.defaultSection : clone(section),
      editingPlan: items.find(plan => plan.id === id),
    };
    navigationState.set(state);
    navigate(withPage("/editor/pricing/item", pageId));
  };

  // a copy of the plan right under it, with everything it has
  const duplicatePlan = (id: string) => {
    const index = items.findIndex(plan => plan.id === id);
    if (index < 0 || isFull) return;
    // a plain copy (one plan featured is enough), its name cut so the copy fits the limit of a name
    const original = clone<PricingItem>(items[index]);
    delete original.featured;
    delete original.featuredLabel;
    const suffix = t(`${prefix}.form.copyOf`, { title: "" });
    const copy: PricingItem = {
      ...original,
      id: uuidv4(),
      title: t(`${prefix}.form.copyOf`, {
        title: original.title.slice(0, PRICING_MAX_TITLE - suffix.length).trimEnd(),
      }),
    };
    setSection({ ...section, items: [...items.slice(0, index + 1), copy, ...items.slice(index + 1)] });
  };

  const removePlan = () => {
    setSection({ ...section, items: items.filter(({ id }) => id !== idRemoving) });
    setIdRemoving(undefined);
  };

  // the list gives back its rows (reordered, shown or hidden): each plan keeps its own fields
  const onRowsUpdate = (rows: Item[]) =>
    setSection({
      ...section,
      items: rows.flatMap(({ id, active }) => {
        const plan = items.find(existing => existing.id === id);
        return plan ? [active === undefined ? plan : { ...plan, active }] : [];
      }),
    });

  const hasUnsavedChangesWrapper = (): boolean => {
    const defaultSection = pricingNavigationState.current?.defaultSection;
    return defaultSection?.type === "pricing" ? !deepEqual(section, defaultSection) : hasUnsavedChanges();
  };

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.header.title`)}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChangesWrapper}
      />
      {idRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.actions.remove.confirmModal.title`)}
          cancelTitle={t(`${prefix}.actions.remove.confirmModal.cancelButton`)}
          confirmButtonTitle={t(`${prefix}.actions.remove.confirmModal.confirmButton`)}
          onCancel={() => setIdRemoving(undefined)}
          onConfirm={removePlan}
        />
      )}
      <Form aria-label={t(`${prefix}.header.title`)} onSubmit={event => event.preventDefault()}>
        <Box borderBottom>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              maxLength={PRICING_MAX_PAGE_TITLE}
              value={section.pageTitle || ""}
              onChange={e => setSection({ ...section, pageTitle: e.target.value })}
              label={t(`${prefix}.form.pageTitle`)}
            />
          </FormGroup>
        </Box>
        <Box borderBottom>
          <LabelText as="p">{t(`${prefix}.form.currency.label`)}</LabelText>
          <CurrencySelect
            label={t(`${prefix}.form.currency.label`)}
            value={currency}
            onChange={value => setSection({ ...section, currency: value })}
          />
        </Box>
        <Box>
          <ItemsTable
            onSortUpdate={onRowsUpdate}
            onAddClick={() => goEditItemPage()}
            onEditClick={goEditItemPage}
            onRemoveClick={setIdRemoving}
            onDuplicateClick={isFull ? undefined : duplicatePlan}
            items={items.map(toRow)}
            title={t(`${prefix}.form.table.title`)}
            addButtonTitle={t(`${prefix}.form.table.addButton`)}
          />
          {isFull && <LimitNote>{t(`${prefix}.form.limit`, { count: PRICING_MAX_ITEMS })}</LimitNote>}
        </Box>
        <SaveBar onSave={saveSection} hasUnsavedChanges={hasUnsavedChangesWrapper} />
      </Form>
    </Container>
  );
};

export default PricingPage;
